import React, { useState, useEffect } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../api/client";
import { COLORS, FONT } from "../theme";
import Icon from "../components/ui/Icon";
import pacote from "../../package.json";

const VERSAO_SISTEMA = pacote.version;

const INTERVALO_PENDENCIAS_MS = 60000;

const BREAKPOINT_MOBILE = 860;

function capitalizar(texto) {
  return texto.charAt(0).toUpperCase() + texto.slice(1);
}

// Relógio/data da topbar — atualiza sozinho a cada segundo, sem depender de
// nenhum dado do servidor (hora do próprio navegador da pessoa).
function useRelogio() {
  const [agora, setAgora] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setAgora(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  return {
    hora: agora.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
    dataExtenso: capitalizar(agora.toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "long" })),
  };
}

// Agrupado em seções — Dashboard/Aprovações (uso diário do gestor) ficam
// soltos no topo sem cabeçalho, o resto é agrupado por assunto em seções
// recolhíveis. Uma seção some inteira (cabeçalho incluso) se nenhum item dela
// for visível pro perfil logado, pra não deixar título solto sem nada embaixo.
// `perfil` aceita uma string única ou um array de perfis permitidos.
function perfilPermitido(perfil, perfilUsuario) {
  if (!perfil) return true;
  return Array.isArray(perfil) ? perfil.includes(perfilUsuario) : perfil === perfilUsuario;
}

// Gestor e Administrador (admin_empresa) têm exatamente o mesmo menu por
// enquanto (pedido do usuário 2026-09-17: "até então devem ser as mesmas,
// depois eu defino o que tirar do gestor") — qualquer item pensado só pra um
// dos dois ganha os dois perfis juntos, nunca um perfil sozinho.
const NAV_GRUPOS = [
  {
    secao: null,
    itens: [
      { to: "/dashboard", label: "Dashboard", icon: "grid", perfil: ["gestor", "admin_empresa"] },
    ],
  },
  {
    // Gestor também bate o próprio ponto (geralmente pelo celular) — juntar
    // com Meu Histórico numa aba só deixa claro que isso é "sobre mim", à
    // parte da administração do resto da equipe (que fica em Funcionários).
    secao: "Meu Ponto",
    icone: "clock",
    itens: [
      { to: "/bater-ponto", label: "Bater Ponto", icon: "clock", perfil: ["gestor", "admin_empresa"] },
      { to: "/", label: "Bater Ponto", icon: "clock", end: true, perfil: "funcionario" },
      { to: "/meu-historico", label: "Meu Histórico", icon: "calendar", perfil: ["gestor", "funcionario", "admin_empresa"] },
    ],
  },
  {
    // Promovida de sub-item dentro de "Equipe" pra seção própria, logo
    // abaixo de "Meu Ponto" (pedido do usuário 2026-09-17). Hora Extra e
    // Atrasos e Faltas vieram do grupo solto do topo pra cá no mesmo dia.
    // "Cadastro" saiu daqui — fica só dentro de "Cadastros" agora.
    secao: "Funcionários",
    icone: "users",
    itens: [
      { to: "/funcionarios/historico", label: "Folha de Ponto", icon: "calendar", perfil: ["gestor", "admin_empresa"] },
      { to: "/hora-extra", label: "Hora Extra", icon: "checkCircle", perfil: ["gestor", "admin_empresa"] },
      // Era um submenu com "Justificativas" (aprovação do antigo "Lançar
      // Esquecimento") — removido junto da funcionalidade em si (ver
      // routes/ponto.js), virou link direto já que sobrou só um item.
      { to: "/atrasos-faltas", label: "Atrasos e Faltas", icon: "alertCircle", perfil: ["gestor", "admin_empresa"] },
      { to: "/afastamentos", label: "Afastamentos/Férias", icon: "heart", perfil: ["gestor", "admin_empresa"] },
    ],
  },
  {
    // Postos e Feriados vieram de dentro de "Equipe". Cadastro de Funcionário
    // (que tinha ficado duplicado, também dentro de "Funcionários" acima) foi
    // removido de lá — agora só existe aqui (pedido do usuário 2026-09-17).
    secao: "Cadastros",
    icone: "briefcase",
    itens: [
      { to: "/funcionarios", label: "Cadastro de Funcionário", icon: "users", end: true, perfil: ["gestor", "admin_empresa"] },
      { to: "/postos", label: "Postos", icon: "mapPin", perfil: ["gestor", "admin_empresa"] },
      { to: "/feriados", label: "Feriados", icon: "calendar", perfil: ["gestor", "admin_empresa"] },
    ],
  },
  {
    secao: "Operacional",
    icone: "route",
    itens: [
      { to: "/fazer-rota", label: "Fazer Rota", icon: "route", perfil: ["gestor", "funcionario", "admin_empresa"] },
      { to: "/rotas", label: "Rotas de Serviço", icon: "route", perfil: ["gestor", "admin_empresa"] },
      { to: "/historico-rotas", label: "Histórico de Rotas", icon: "fileText", perfil: ["gestor", "admin_empresa"] },
    ],
  },
  {
    secao: "Relatórios",
    icone: "barChart",
    itens: [
      { to: "/relatorios", label: "Relatórios", icon: "barChart", perfil: ["gestor", "admin_empresa"] },
      { to: "/auditoria", label: "Auditoria", icon: "fileText", perfil: ["gestor", "admin_empresa"] },
    ],
  },
];

// Uma seção recolhível abre sozinha quando a rota atual é de um item dela
// (senão a pessoa cai numa tela sem ver o item ativo, escondido atrás de uma
// aba fechada) — olha tanto o `to` direto quanto os `children` (ex: Funcionários).
function grupoTemRotaAtiva(grupo, pathname) {
  return grupo.itens.some((item) =>
    item.children ? item.children.some((c) => c.to === pathname) : item.to === pathname
  );
}

// Antes era mais uma seção no menu ("Configurações") — virou o ícone de
// engrenagem ao lado do nome do usuário, num popover pequeno. Mesmo conteúdo
// de sempre, só a forma de abrir que mudou.
const ITENS_CONFIGURACAO = [
  { to: "/meus-dados", label: "Meus Dados", icon: "user" },
  { to: "/empresas", label: "Empresas", icon: "briefcase", perfil: ["gestor", "admin_empresa"] },
  { to: "/usuarios", label: "Usuários", icon: "user", perfil: ["gestor", "admin_empresa"] },
  { to: "/parametros", label: "Parâmetros", icon: "settings", perfil: ["gestor", "admin_empresa"] },
  { to: "/ajuda", label: "Ajuda", icon: "helpCircle" },
];

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

// Abas fixas embaixo no mobile — só os destinos mais usados de cada perfil
// (o resto continua acessível pela gaveta, aberta pelo ☰ ou pela aba "Mais").
// Funcionário já cabe inteiro em 4 abas (é a mesma lista de itens sem
// restrição de perfil), então não precisa de "Mais".
function tabsMobile(perfil) {
  // Gestor e Administrador têm o mesmo menu por enquanto (ver NAV_GRUPOS) —
  // mesmas abas fixas do mobile também.
  if (perfil === "gestor" || perfil === "admin_empresa") {
    return [
      { to: "/dashboard", label: "Dashboard", icon: "grid" },
      { to: "/bater-ponto", label: "Ponto", icon: "clock" },
      { to: "/hora-extra", label: "Hora Extra", icon: "checkCircle", badgeKey: "hora-extra" },
      { to: "/funcionarios", label: "Equipe", icon: "users" },
      { mais: true, label: "Mais", icon: "moreHorizontal" },
    ];
  }
  return [
    { to: "/", label: "Ponto", icon: "clock", end: true },
    { to: "/meu-historico", label: "Histórico", icon: "calendar" },
    { to: "/meus-dados", label: "Perfil", icon: "user" },
    { to: "/ajuda", label: "Ajuda", icon: "helpCircle" },
  ];
}

function tabItemStyle(isActive) {
  return {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: 3,
    padding: "8px 4px 7px",
    textDecoration: "none",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    color: isActive ? COLORS.primary : COLORS.textLight,
  };
}

function itemStyle(isActive, corInativa = COLORS.sidebarText) {
  return {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    borderRadius: 10,
    fontSize: 13.5,
    fontWeight: isActive ? 600 : 500,
    textDecoration: "none",
    color: isActive ? COLORS.white : corInativa,
    background: isActive ? COLORS.sidebarActiveBg : "transparent",
    letterSpacing: "0.01em",
    cursor: "pointer",
    border: "none",
    width: "100%",
    textAlign: "left",
    transition: "background .15s, color .15s",
  };
}

function navStyle({ isActive }) {
  return itemStyle(isActive);
}

// Dashboard/Aprovações (grupo solto do topo, sem seção) — mesma cor/peso dos
// títulos de seção (Meu Ponto, Equipe...) pra não destoarem ficando mais
// brancos que o resto do menu.
function navStylePrincipal({ isActive }) {
  return itemStyle(isActive, "rgba(255,255,255,0.55)");
}

const LABEL_PERFIL = { gestor: "Gestor", funcionario: "Funcionário", admin_empresa: "Administrador" };

export default function AppLayout() {
  const { usuario, token, logout, trocarModo } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { hora, dataExtenso } = useRelogio();
  const [abertos, setAbertos] = useState({});
  // undefined = ainda sem escolha manual (usa o padrão: fechada, a menos que
  // a rota atual esteja dentro dela); true/false = a pessoa já clicou.
  const [secoesAbertasManual, setSecoesAbertasManual] = useState({});
  const [configAberta, setConfigAberta] = useState(false);
  const [isMob, setIsMob] = useState(window.innerWidth < BREAKPOINT_MOBILE);
  const [drawerAberto, setDrawerAberto] = useState(false);
  const [pendencias, setPendencias] = useState(0);
  const [temRotaEscalada, setTemRotaEscalada] = useState(false);
  const [trocandoModo, setTrocandoModo] = useState(false);
  const [empresaPrincipal, setEmpresaPrincipal] = useState(null);
  const [logoEmpresaQuadrada, setLogoEmpresaQuadrada] = useState(false);

  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < BREAKPOINT_MOBILE);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  // Contagem de aprovações pendentes no item do menu — sem isso o gestor só
  // percebe que tem pendência se passar pelo Dashboard primeiro.
  useEffect(() => {
    if (!["gestor", "admin_empresa"].includes(usuario?.perfil)) return;
    let cancelado = false;
    const carregar = async () => {
      try {
        const data = await apiFetch("GET", "/api/ponto/hora-extra-pendentes", undefined, token);
        if (!cancelado) setPendencias(data.length);
      } catch {
        // silencioso — badge é só um indicador a mais, não pode travar a navegação
      }
    };
    carregar();
    const id = setInterval(carregar, INTERVALO_PENDENCIAS_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [usuario?.perfil, token, location.pathname]);

  // "Fazer Rota" só aparece no menu pra quem realmente tem alguma rota
  // escalada — sem essa checagem, todo funcionário/gestor veria a aba mesmo
  // sem nada pra fazer nela.
  useEffect(() => {
    if (!["gestor", "funcionario", "admin_empresa"].includes(usuario?.perfil)) return;
    let cancelado = false;
    const carregar = async () => {
      try {
        const data = await apiFetch("GET", "/api/rotas-execucoes/minhas-rotas", undefined, token);
        if (!cancelado) setTemRotaEscalada(data.length > 0);
      } catch {
        // silencioso — pior caso, a aba fica escondida até a próxima checagem
      }
    };
    carregar();
    const id = setInterval(carregar, INTERVALO_PENDENCIAS_MS);
    return () => { cancelado = true; clearInterval(id); };
  }, [usuario?.perfil, token]);

  // Logo da empresa na topbar — busca uma vez só, pega a primeira empresa do
  // grupo que tiver logo cadastrada (a maioria dos tenants tem só uma).
  useEffect(() => {
    let cancelado = false;
    const carregar = async () => {
      try {
        const data = await apiFetch("GET", "/api/empresas", undefined, token);
        if (!cancelado) setEmpresaPrincipal(data.find((e) => e.logo_path) || data[0] || null);
      } catch {
        // silencioso — sem logo, a topbar cai de volta pro ícone + nome do tenant
      }
    };
    carregar();
  }, [token]);

  // Fecha a gaveta e o popover de configurações automaticamente ao navegar —
  // sem isso ficariam abertos por cima da tela nova depois de tocar num item.
  useEffect(() => { setDrawerAberto(false); setConfigAberta(false); }, [location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // `perfil_real` só existe no token quando a pessoa está "emprestada" no
  // modo Admin da Empresa (ver trocar-modo em auth.js) — presença dele é o
  // sinal de que o botão deve oferecer voltar, não entrar.
  const empresaLogo = empresaPrincipal?.logo_path
    ? `${API_BASE_URL}/api/empresas/${empresaPrincipal.id}/logo?v=${empresaPrincipal.logo_path}`
    : null;

  const emModoAdmin = !!usuario?.perfil_real;
  const podeAlternarModo = usuario?.pode_admin_empresa || emModoAdmin;
  const handleTrocarModo = async () => {
    setTrocandoModo(true);
    try {
      await trocarModo(emModoAdmin ? "normal" : "admin_empresa");
      navigate("/");
    } catch (e) {
      window.alert(e.message);
    } finally {
      setTrocandoModo(false);
    }
  };

  return (
    <div className="app-shell" style={{ display: "flex", fontFamily: FONT, background: COLORS.bg }}>
      {/* A prop `style` não tem como expressar @media — só um <style> de verdade
          resolve isso sem trazer uma lib de CSS-in-JS pro projeto. */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          .print-area { overflow: visible !important; padding: 0 !important; }
          @page { margin: 10mm; }
          .tabela-mes-print th, .tabela-mes-print td { padding: 3px 8px !important; font-size: 10px !important; }
          .tabela-mes-print tr { page-break-inside: avoid; }
          .tabela-mes-print thead { display: table-header-group; }
        }

        /* 100vh no Safari mobile inclui a área escondida atrás da barra de
           endereço/toolbar, que encolhe e expande ao rolar — isso empurrava
           conteúdo fixo no fim da tela (rodapé da gaveta, abas de baixo) pra
           debaixo da barra do navegador. 100dvh acompanha a altura visível
           de verdade. Fallback 100vh primeiro pro navegador que não suporta dvh. */
        .app-shell, .app-shell-drawer { height: 100vh; height: 100dvh; }

        /* Layout mestre-detalhe (lista lateral fixa + conteúdo) usado em
           Funcionários e Folha de Ponto — no mobile empilha em vez de
           espremer a lista de 300px lado a lado com o conteúdo. */
        .split-layout { display: flex; gap: 20px; align-items: flex-start; }
        .split-layout-sidebar { width: 300px; flex-shrink: 0; }

        /* Cabeçalho em onda (Bater Ponto) — estoura o padding do <main> pra
           ficar "de ponta a ponta" dentro da área de conteúdo. O padding do
           <main> é diferente em cada modo (ver isMob mais abaixo), então a
           margem negativa que cancela ele também precisa mudar aqui — não
           dá pra fazer isso com style inline (JS não sabe o breakpoint real
           do CSS sem duplicar o BREAKPOINT_MOBILE numa media query igual). */
        .onda-full-bleed { margin: -32px -32px 0; }
        @media (max-width: ${BREAKPOINT_MOBILE}px) {
          .onda-full-bleed { margin: -72px -16px 0; }
        }

        @media (max-width: ${BREAKPOINT_MOBILE}px) {
          .split-layout { flex-direction: column; }
          .split-layout-sidebar { width: 100%; }
          /* Listas com altura fixa em pixel (pensadas pro tamanho de tela do
             desktop) podiam cortar o último item numa tela de celular menor
             — no mobile a rolagem da própria página já resolve, sem precisar
             de uma caixinha de rolagem interna com altura travada. */
          .mobile-scroll-relax { max-height: none !important; }
        }

        /* Data/hora da topbar só cabe confortável em telas mais largas — no
           desktop estreito (perto do ponto onde vira mobile) some primeiro,
           antes de qualquer coisa apertar ou quebrar linha. */
        @media (max-width: 1100px) {
          .topbar-relogio { display: none !important; }
        }
      `}</style>

      {isMob && (
        <div
          className="no-print"
          style={{
            position: "fixed", top: 0, left: 0, right: 0, height: 56, zIndex: 30,
            background: COLORS.sidebarBg, display: "flex", alignItems: "center", gap: 14, padding: "0 8px 0 4px",
          }}
        >
          <button
            onClick={() => setDrawerAberto(true)}
            aria-label="Abrir menu"
            style={{ background: "transparent", border: "none", color: COLORS.white, cursor: "pointer", padding: 10, display: "flex" }}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <img src={`${process.env.PUBLIC_URL}/logo-pontozipar.png`} alt="PontoZipar" style={{ height: 28, objectFit: "contain" }} />
        </div>
      )}

      {isMob && drawerAberto && (
        <div
          onClick={() => setDrawerAberto(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 35 }}
        />
      )}

      <aside
        className={isMob ? "no-print app-shell-drawer" : "no-print"}
        style={{
          width: 250,
          flexShrink: 0,
          background: COLORS.sidebarBg,
          display: "flex",
          flexDirection: "column",
          padding: "28px 16px",
          ...(isMob
            ? {
                position: "fixed",
                top: 0,
                bottom: 0,
                left: 0,
                zIndex: 40,
                transform: drawerAberto ? "translateX(0)" : "translateX(-100%)",
                transition: "transform .25s ease",
                boxShadow: drawerAberto ? "4px 0 24px rgba(0,0,0,0.3)" : "none",
              }
            : {
                // Cantos bem arredondados + um "contorno" sólido (não borrado,
                // igual sombra normal) na cor clara do gradiente da logo —
                // dá o efeito de cartão duplo/adesivo, sem precisar de um
                // segundo elemento empilhado atrás.
                margin: "12px 0 24px 12px",
                borderRadius: 28,
                boxShadow: `8px 8px 0 0 ${COLORS.sidebarAccent}`,
              }),
        }}
      >
        <div style={{ padding: "0 0 32px", display: "flex", justifyContent: "center" }}>
          <img src={`${process.env.PUBLIC_URL}/logo-pontozipar.png`} alt="PontoZipar" style={{ height: 88, objectFit: "contain" }} />
        </div>

        <nav style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, overflowY: "auto" }}>
          {NAV_GRUPOS.map((grupo) => {
            const itensVisiveis = grupo.itens.filter((item) => {
              if (!perfilPermitido(item.perfil, usuario?.perfil)) return false;
              if (item.to === "/fazer-rota" && !temRotaEscalada) return false;
              return true;
            });
            if (itensVisiveis.length === 0) return null;
            // Padrão: fechada. Só abre sozinha se a rota atual estiver dentro
            // dela, ou se a pessoa já tiver clicado pra abrir manualmente.
            const manual = secoesAbertasManual[grupo.secao];
            const secaoAberta = grupo.secao
              ? (manual !== undefined ? manual : grupoTemRotaAtiva(grupo, location.pathname))
              : true;
            return (
              <div key={grupo.secao || "principal"} style={{ marginTop: grupo.secao ? 18 : 0 }}>
                {grupo.secao && (
                  <button
                    onClick={() => setSecoesAbertasManual({ ...secoesAbertasManual, [grupo.secao]: !secaoAberta })}
                    style={{
                      display: "flex", alignItems: "center", gap: 12, width: "100%",
                      background: "none", border: "none", cursor: "pointer", padding: "10px 14px", marginBottom: 4,
                    }}
                  >
                    <Icon name={grupo.icone} size={17} color="rgba(255,255,255,0.55)" />
                    {/* Mesmo tamanho/peso dos itens normais (Dashboard etc.) — a versão
                        antiga (11.5px, uppercase, letter-spacing largo) ficava "pixelada"
                        de tão pequena e apertada. A cor mais apagada já distingue o
                        cabeçalho de seção sem precisar de truque tipográfico. */}
                    <span style={{ flex: 1, textAlign: "left", fontSize: 13.5, fontWeight: 700, color: "rgba(255,255,255,0.55)", letterSpacing: "0.01em" }}>
                      {grupo.secao}
                    </span>
                    <Icon
                      name="chevronDown"
                      size={13}
                      color="rgba(255,255,255,0.4)"
                      style={{ transform: secaoAberta ? "none" : "rotate(-90deg)", transition: "transform .15s" }}
                    />
                  </button>
                )}
                {secaoAberta && (
                <div
                  style={
                    grupo.secao
                      ? { display: "flex", flexDirection: "column", gap: 2, marginLeft: 14, paddingLeft: 10, borderLeft: "2px solid rgba(255,255,255,0.14)" }
                      // Dashboard/Aprovações — mesmo espaçamento visual das seções
                      // (que respiram mais por causa do cabeçalho); com gap:2
                      // (igual itens dentro de uma seção) ficavam grudados um no
                      // outro, destoando do resto.
                      : { display: "flex", flexDirection: "column", gap: 8 }
                  }
                >
                  {itensVisiveis.map((item) => {
                    if (item.children) {
                      const childrenVisiveis = item.children.filter((c) => perfilPermitido(c.perfil, usuario?.perfil));
                      const algumFilhoAtivo = childrenVisiveis.some((c) => location.pathname === c.to);
                      // Abre sozinho quando a rota atual é de um dos filhos (senão a
                      // pessoa cai numa tela sem ver o item ativo no menu); fora
                      // disso só abre/fecha manualmente, não fica ligado por padrão.
                      const aberto = !!abertos[item.label] || algumFilhoAtivo;
                      return (
                        <div key={item.label}>
                          <button
                            onClick={() => setAbertos({ ...abertos, [item.label]: !aberto })}
                            style={itemStyle(algumFilhoAtivo)}
                          >
                            <Icon name={item.icon} size={17} />
                            {item.label}
                            <Icon
                              name="chevronDown"
                              size={12}
                              style={{ marginLeft: "auto", opacity: 0.7, transform: aberto ? "none" : "rotate(-90deg)", transition: "transform .15s" }}
                            />
                          </button>
                          {aberto && (
                            <div
                              style={{
                                display: "flex", flexDirection: "column", gap: 2, marginTop: 2,
                                marginLeft: 14, paddingLeft: 10, borderLeft: "2px solid rgba(255,255,255,0.14)",
                              }}
                            >
                              {childrenVisiveis.map((child) => (
                                <NavLink key={child.to} to={child.to} end={child.end} style={navStyle}>
                                  {child.label}
                                </NavLink>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                    return (
                      <NavLink key={item.to} to={item.to} end={item.end} style={grupo.secao ? navStyle : navStylePrincipal}>
                        <Icon name={item.icon} size={17} />
                        {item.label}
                        {item.to === "/hora-extra" && pendencias > 0 && (
                          <span
                            style={{
                              marginLeft: "auto", background: COLORS.danger, color: "#fff", fontSize: 11, fontWeight: 700,
                              borderRadius: 999, minWidth: 18, height: 18, padding: "0 5px",
                              display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                          >
                            {pendencias}
                          </span>
                        )}
                      </NavLink>
                    );
                  })}
                </div>
                )}
              </div>
            );
          })}
        </nav>

      </aside>

      <div style={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
        {!isMob && (
          <header
            className="no-print"
            style={{
              flexShrink: 0,
              margin: "12px 20px 0 20px",
              borderRadius: 28,
              background: COLORS.sidebarBg,
              boxShadow: `8px 8px 0 0 ${COLORS.sidebarAccent}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              padding: "14px 22px",
              position: "relative",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
              {empresaLogo ? (
                <img
                  src={empresaLogo}
                  alt=""
                  // Logo quadrada (ex: 500x500) "encolhia" demais pra caber
                  // numa caixa pensada pra logo retangular (ex: 500x250) —
                  // pedido do usuário 2026-10-15: detecta a proporção real
                  // da imagem no load e usa uma caixa maior só pro caso
                  // quadrado, já que "contain" limita pela menor dimensão.
                  onLoad={(e) => {
                    const { naturalWidth: w, naturalHeight: h } = e.target;
                    setLogoEmpresaQuadrada(h > 0 && Math.abs(w / h - 1) < 0.2);
                  }}
                  style={
                    logoEmpresaQuadrada
                      ? { height: 56, maxWidth: 120, objectFit: "contain", borderRadius: 6, flexShrink: 0 }
                      : { height: 26, maxWidth: 120, objectFit: "contain", borderRadius: 6, flexShrink: 0 }
                  }
                />
              ) : (
                <Icon name="briefcase" size={16} color="rgba(255,255,255,0.6)" />
              )}
              <span
                style={{
                  fontSize: 13.5, fontWeight: 700, color: COLORS.white,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}
                title={usuario?.tenant_nome || ""}
              >
                {usuario?.tenant_nome || "PontoZipar"}
              </span>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14, flexShrink: 0 }}>
              {podeAlternarModo && (
                <button
                  onClick={handleTrocarModo}
                  disabled={trocandoModo}
                  style={{
                    background: emModoAdmin ? "transparent" : COLORS.primary,
                    border: emModoAdmin ? `1.5px solid ${COLORS.sidebarBorder}` : "none",
                    borderRadius: 8,
                    padding: "7px 12px",
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: emModoAdmin ? COLORS.sidebarText : COLORS.white,
                    cursor: trocandoModo ? "default" : "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  {trocandoModo
                    ? "Trocando..."
                    : emModoAdmin
                    ? `Voltar pra ${LABEL_PERFIL[usuario.perfil_real] || "Funcionário"}`
                    : "Acessar modo Administrador"}
                </button>
              )}

              {/* Data, hora e versão agrupadas perto do usuário — antes ficavam
                  espalhadas (versão do lado do nome da empresa, relógio centralizado). */}
              <div
                className="topbar-relogio"
                style={{
                  display: "flex", alignItems: "center", gap: 14, flexShrink: 0,
                  paddingRight: 14, borderRight: `1px solid ${COLORS.sidebarBorder}`,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <Icon name="calendar" size={14} color="rgba(255,255,255,0.5)" />
                  <span style={{ fontSize: 12.5, fontWeight: 600, color: "rgba(255,255,255,0.75)", whiteSpace: "nowrap" }}>
                    {dataExtenso}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                  <Icon name="clock" size={14} color="rgba(255,255,255,0.5)" />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: COLORS.white, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
                    {hora}
                  </span>
                </div>
                <span style={{ fontSize: 10.5, color: "rgba(255,255,255,0.35)", whiteSpace: "nowrap" }}>
                  v{VERSAO_SISTEMA}
                </span>
              </div>

              <div style={{ textAlign: "right", minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.white, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160 }}>
                  {usuario?.nome}
                </div>
                <div style={{ fontSize: 11, color: COLORS.sidebarTextLight }}>{LABEL_PERFIL[usuario?.perfil] || usuario?.perfil}</div>
              </div>

              <button
                onClick={() => setConfigAberta(!configAberta)}
                aria-label="Configurações"
                style={{
                  flexShrink: 0, width: 34, height: 34, borderRadius: 9,
                  background: configAberta ? COLORS.sidebarActiveBg : "transparent", border: "none",
                  color: COLORS.sidebarText, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                <Icon name="settings" size={18} />
              </button>

              <button
                onClick={handleLogout}
                style={{
                  background: "transparent",
                  border: `1.5px solid ${COLORS.sidebarBorder}`,
                  borderRadius: 8,
                  padding: "7px 14px",
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: COLORS.sidebarText,
                  cursor: "pointer",
                  whiteSpace: "nowrap",
                }}
              >
                Sair
              </button>
            </div>

            {configAberta && (
              <>
                <div onClick={() => setConfigAberta(false)} style={{ position: "fixed", inset: 0, zIndex: 45 }} />
                <div
                  style={{
                    position: "absolute", top: "100%", right: 22, marginTop: 8, zIndex: 46,
                    background: COLORS.surface, borderRadius: 14, boxShadow: "0 14px 28px rgba(11,22,54,0.22)",
                    padding: 6, minWidth: 190,
                  }}
                >
                  {ITENS_CONFIGURACAO.filter((item) => perfilPermitido(item.perfil, usuario?.perfil)).map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      onClick={() => setConfigAberta(false)}
                      style={({ isActive }) => ({
                        display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 9,
                        fontSize: 13.5, fontWeight: 600, textDecoration: "none",
                        color: isActive ? COLORS.primary : COLORS.text,
                        background: isActive ? COLORS.primaryBg : "transparent",
                      })}
                    >
                      <Icon name={item.icon} size={16} />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </>
            )}
          </header>
        )}

        <main
          className="print-area"
          style={{
            flex: 1, overflow: "auto", padding: isMob ? "16px" : 32,
            paddingTop: isMob ? 72 : 32, paddingBottom: isMob ? 82 : 32,
          }}
        >
        <Outlet />
      </main>
      </div>

      {isMob && (
        <nav
          className="no-print"
          style={{
            position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 30,
            background: COLORS.surface, borderTop: `1px solid ${COLORS.border}`,
            display: "flex", boxShadow: "0 -2px 16px rgba(16,26,61,0.08)",
            paddingBottom: "env(safe-area-inset-bottom, 0px)",
          }}
        >
          {tabsMobile(usuario?.perfil).map((tab) =>
            tab.mais ? (
              <button
                key="mais"
                onClick={() => setDrawerAberto(true)}
                style={tabItemStyle(drawerAberto)}
              >
                <Icon name={tab.icon} size={20} />
                <span style={{ fontSize: 10.5, fontWeight: 600, whiteSpace: "nowrap" }}>{tab.label}</span>
              </button>
            ) : (
              <NavLink key={tab.to} to={tab.to} end={tab.end} style={({ isActive }) => tabItemStyle(isActive)}>
                {({ isActive }) => (
                  <>
                    <div style={{ position: "relative" }}>
                      <Icon name={tab.icon} size={20} />
                      {tab.badgeKey === "hora-extra" && pendencias > 0 && (
                        <span
                          style={{
                            position: "absolute", top: -3, right: -6, width: 8, height: 8, borderRadius: "50%",
                            background: COLORS.danger, border: `1.5px solid ${COLORS.surface}`,
                          }}
                        />
                      )}
                    </div>
                    <span style={{ fontSize: 10.5, fontWeight: isActive ? 700 : 600, whiteSpace: "nowrap" }}>
                      {tab.label}
                    </span>
                  </>
                )}
              </NavLink>
            )
          )}
        </nav>
      )}
    </div>
  );
}
